import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import _ from 'lodash';
import { theme } from '../../../theme/theme';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { authActions } from '../../../redux/auth/authSlice';
import { RootState } from '../../../redux/store';
import WarningSvg from '../../../assets/img/red-warning.svg';
import DeleteSvg from '../../../assets/img/chip-remove-icon.svg';
import { UsageType } from '../../../utils/enums/usage-type.enum';
import { useAuth } from '../../../context/AuthContext';

interface SubordinateEmailMultiSelectProps {
  emailList: { id: string; email: string }[];
  setEmailList: Dispatch<SetStateAction<{ id: string; email: string }[]>>;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
}

const SubordinateEmailMultiSelect = ({
  emailList,
  setEmailList,
  error,
  setError,
}: SubordinateEmailMultiSelectProps) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const componentStatus = useAppSelector((state: RootState) => state.auth?.componentStatus);
  const isEmailAlreadyExists = _.get(componentStatus, 'isEmailAlreadyExists', undefined) as boolean;
  const [emailInput, setEmailInput] = useState('');
  const [addedEmail, setAddedEmail] = useState('');
  const [errorEmails, setErrorEmails] = useState<string[]>([]);
  const userLimit = user.usage.find((item: { type: UsageType }) => item.type === UsageType.USER_LIMIT).limit;
  const subordinateUsage = user.usage.find((item: { type: UsageType }) => item.type === UsageType.USER_LIMIT).usage;
  const remainingSeats = userLimit - subordinateUsage;
  const emailRegex = /^[a-zA-Z0-9._%+-]{1,200}@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const checkEmailTaken = (input: string, isUsername: boolean) => {
    dispatch(authActions.checkUsernameOrEmailTakenAsync({ input, isUsername }));
  };
  const { isNccUser } = user;

  useEffect(() => {
    if (addedEmail) {
      if (isEmailAlreadyExists) {
        setError('Email Address is already taken');
        setErrorEmails((prev) => {
          if (!prev.includes(addedEmail)) {
            return [...prev, addedEmail];
          }
          return prev;
        });
      } else if (!isEmailAlreadyExists && !error) {
        setErrorEmails((prev) => prev.filter((email) => email !== addedEmail));
        setError('');
      }
    }
  }, [isEmailAlreadyExists, addedEmail]);

  useEffect(() => {
    checkEmailTaken(addedEmail, false);
  }, [addedEmail]);

  const handleAddEmail = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && emailInput.trim()) {
      if (error) return;
      const trimmedEmail = emailInput.trim();
      const emailId = crypto.randomUUID();
      setAddedEmail(trimmedEmail);
      if (!emailRegex.test(trimmedEmail)) {
        setError('Invalid email address.');
        setErrorEmails((prev) => [...prev, trimmedEmail]);
        setEmailList((prev) => [...prev, { id: emailId, email: trimmedEmail }]);
        setEmailInput('');
        return;
      }
      setEmailList((prevEmails) => {
        const updatedEmails = [...prevEmails, { id: emailId, email: trimmedEmail }];
        const hasDuplicates =
          updatedEmails.filter((item) => item.email.toLowerCase() === trimmedEmail.toLowerCase()).length > 1;

        if (hasDuplicates) {
          setError('Duplicate emails detected.');
          setErrorEmails((prev) => [...prev, trimmedEmail]);
        }

        return updatedEmails;
      });
      checkEmailTaken(trimmedEmail, false);
      setEmailInput('');
    }
  };

  const handleDeleteEmail = (emailIdToDelete: string) => {
    setEmailList((prevEmails) => {
      const updatedEmails = prevEmails.filter(({ id }) => id !== emailIdToDelete);

      const deletedEmail = prevEmails.find(({ id }) => id === emailIdToDelete)?.email;

      if (deletedEmail) {
        setErrorEmails((prev) => prev.filter((email) => email !== deletedEmail));
      }

      return updatedEmails;
    });

    setError(''); // Clear error if any
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(event.target.value);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', paddingX: '24px' }}>
      <Typography variant="textH6" style={{ marginTop: '20px' }}>
        Enter Subordinate Emails
      </Typography>
      <Box
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          marginTop: '10px',
          padding: '8px',
          border: '1px solid #DCE0E5',
          borderRadius: '8px',
          cursor: 'text',
          height: isNccUser ? '265px' : '76px',
          alignContent: 'flex-start',
          overflow: 'auto',
        }}
        onClick={() => document.getElementById('email-input')?.focus()}
      >
        {emailList.map(({ id, email }) => (
          <Chip
            key={id}
            label={email}
            onDelete={() => handleDeleteEmail(id)}
            deleteIcon={<Box data-testid="delete-icon" component="img" src={DeleteSvg} />}
            variant="outlined"
            sx={{
              backgroundColor: '#F6F7F9',
              border: '1px solid B6C1CA',
              borderRadius: '8px',
              borderColor: errorEmails.includes(email) && errorEmails.length > 0 ? 'red' : '#B6C1CA',
              ...theme.typography.textH6,
            }}
          />
        ))}
        <input
          id="email-input"
          value={emailInput}
          onChange={handleChange}
          onKeyDown={handleAddEmail}
          disabled={!!error || (remainingSeats <= emailList.length && !user.isNccUser)}
          style={{
            border: 'none',
            outline: 'none',
            flexGrow: 1,
            minWidth: '150px',
            padding: '7px',
            ...theme.typography.tableRowRegular,
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: error ? 'space-between' : 'flex-end',
          alignItems: 'center',
          marginTop: '10px',
        }}
      >
        {error && (
          <Box display="flex" alignItems="center" gap="5px">
            <Box component="img" src={WarningSvg} />
            <Typography variant="footnoteRegular2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {!user.isNccUser && (
          <Typography variant="footnoteRegular2" color="#525E6F">
            Remaining Seats: {remainingSeats - emailList.length}/{userLimit}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SubordinateEmailMultiSelect;
